// REPORTS DASHBOARD - Main Page
// Comprehensive reporting and analytics dashboard for timesheets.
// Phase 7: Reports & Analytics

import React, {useState, useEffect} from 'react'
import {Row, Col, Card, Button, Tab, Tabs} from 'react-bootstrap'
import {format, startOfMonth, endOfMonth, subMonths} from 'date-fns'
import {toast} from 'react-toastify'
import {useAuth} from '../../../modules/auth'
import TeamUtilization from './TeamUtilization'
import ProjectAnalysis from './ProjectAnalysis'
import EmployeeProductivity from './EmployeeProductivity'
import ApprovalAnalytics from './ApprovalAnalytics'
import OvertimeAnalysis from './OvertimeAnalysis'

const ReportsDashboard = () => {
  const {currentUser} = useAuth()
  const managerId = currentUser?.id || ''

  // Date range state (default: current month)
  const [startDate, setStartDate] = useState<string>(
    format(startOfMonth(new Date()), 'yyyy-MM-dd')
  )
  const [endDate, setEndDate] = useState<string>(format(endOfMonth(new Date()), 'yyyy-MM-dd'))

  // Active tab
  const [activeTab, setActiveTab] = useState<string>('team')

  // Quick date range presets
  const setDateRange = (preset: string) => {
    const today = new Date()
    let start: Date
    let end: Date = new Date()

    switch (preset) {
      case 'thisMonth':
        start = startOfMonth(today)
        end = endOfMonth(today)
        break
      case 'lastMonth':
        start = startOfMonth(subMonths(today, 1))
        end = endOfMonth(subMonths(today, 1))
        break
      case 'last3Months':
        start = startOfMonth(subMonths(today, 2))
        end = endOfMonth(today)
        break
      case 'last6Months':
        start = startOfMonth(subMonths(today, 5))
        end = endOfMonth(today)
        break
      default:
        start = startOfMonth(today)
        end = endOfMonth(today)
    }

    setStartDate(format(start, 'yyyy-MM-dd'))
    setEndDate(format(end, 'yyyy-MM-dd'))
  }

  // Early return if user not loaded
  if (!currentUser || !managerId) {
    return (
      <div className='d-flex justify-content-center align-items-center' style={{minHeight: '400px'}}>
        <div className='text-center'>
          <div className='spinner-border text-primary mb-3' role='status' />
          <p className='text-muted'>Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <>
      {/* Page Header */}
      <Row className='mb-4'>
        <Col>
          <h3 className='mb-0'>
            Timesheet Reports & Analytics
          </h3>
        </Col>
      </Row>

      {/* Date Range Selector */}
      <Row className='mb-4'>
        <Col>
          <Card className='shadow-sm'>
            <Card.Body>
              <Row className='align-items-end'>
                <Col md={3}>
                  <label className='form-label'>Start Date</label>
                  <input
                    type='date'
                    className='form-control'
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </Col>
                <Col md={3}>
                  <label className='form-label'>End Date</label>
                  <input
                    type='date'
                    className='form-control'
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </Col>
                <Col md={6}>
                  <label className='form-label'>Quick Presets</label>
                  <div className='d-flex gap-2'>
                    <Button size='sm' variant='outline-primary' onClick={() => setDateRange('thisMonth')}>
                      This Month
                    </Button>
                    <Button size='sm' variant='outline-primary' onClick={() => setDateRange('lastMonth')}>
                      Last Month
                    </Button>
                    <Button size='sm' variant='outline-primary' onClick={() => setDateRange('last3Months')}>
                      Last 3 Months
                    </Button>
                    <Button size='sm' variant='outline-primary' onClick={() => setDateRange('last6Months')}>
                      Last 6 Months
                    </Button>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Tabs for Different Reports */}
      <Row>
        <Col>
          <Card className='shadow-sm'>
            <Card.Body>
              <Tabs
                activeKey={activeTab}
                onSelect={(k) => setActiveTab(k || 'team')}
                className='mb-3'
              >
                <Tab eventKey='team' title='Team Utilization'>
                  <TeamUtilization
                    managerId={managerId}
                    startDate={startDate}
                    endDate={endDate}
                  />
                </Tab>

                <Tab eventKey='projects' title='Project Analysis'>
                  <ProjectAnalysis
                    startDate={startDate}
                    endDate={endDate}
                    managerId={managerId}
                  />
                </Tab>

                <Tab eventKey='employee' title='Employee Productivity'>
                  <EmployeeProductivity
                    startDate={startDate}
                    endDate={endDate}
                    managerId={managerId}
                  />
                </Tab>

                <Tab eventKey='approval' title='Approval Analytics'>
                  <ApprovalAnalytics
                    managerId={managerId}
                    startDate={startDate}
                    endDate={endDate}
                  />
                </Tab>

                <Tab eventKey='overtime' title='Overtime Analysis'>
                  <OvertimeAnalysis
                    managerId={managerId}
                    startDate={startDate}
                    endDate={endDate}
                  />
                </Tab>
              </Tabs>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  )
}

export default ReportsDashboard

